/**
 * @file PickingQueueFilters.tsx
 * @description Строка фильтров экрана заданий на сборку — общая для очереди
 * и журнала (phase19.14; phase19.23, A1; phase19.31 шаг О4 — макет (г)).
 *
 * Поиск и скан — одно поле: USB-сканер шлёт номер заказа как клавиатуру.
 * Поле стандартной высоты (48/44, у кладовщика 56 до 1024), первое на
 * телефоне; фокус при открытии — только в режиме кладовщика. Фильтры —
 * `FilterChips` со счётчиками (SC45). Склад — `SelectField` стандартной
 * высоты на широком экране; на телефоне и планшете он в «⋯» заголовка
 * (страница передаёт `showWarehouse={false}`).
 */

import { ScanInputField } from "./ScanInputField";
import { Button } from "../../ui/button";
import { FilterBar } from "../../ui/FilterBar";
import { FilterChips, type FilterChip } from "../../ui/FilterChips";
import { SelectField } from "../../ui/SelectField";
import type { WarehouseDto } from "../../../lib/api-wms";

interface Props<T extends string> {
  /** Пилюли активного режима: «Мои/Все/Недостача» либо «Все/Проведённые/Отменённые». */
  chips: FilterChip<T>[];
  tab: T;
  onTabChange: (tab: T) => void;
  search: string;
  onSearchChange: (value: string) => void;
  warehouses: WarehouseDto[];
  warehouseId: string;
  onWarehouseChange: (id: string) => void;
  loading: boolean;
  /** Показывать выбор склада в строке (широкий экран). */
  showWarehouse: boolean;
  /** Взять фокус в поле скана при открытии (режим кладовщика). */
  autoFocus: boolean;
  /** Подсказка поля поиска: в журнале ищем по загруженным страницам. */
  searchPlaceholder?: string;
}

export function PickingQueueFilters<T extends string>({
  chips,
  tab,
  onTabChange,
  search,
  onSearchChange,
  warehouses,
  warehouseId,
  onWarehouseChange,
  loading,
  showWarehouse,
  autoFocus,
  searchPlaceholder = "Скан или номер заказа",
}: Props<T>) {
  return (
    <div className="flex flex-col gap-2">
      <FilterBar
        search={
          <ScanInputField
            placeholder={searchPlaceholder}
            onSubmit={onSearchChange}
            // Поле возвращает себе фокус, когда снимается блокировка: у владельца
            // за компьютером не блокируем — иначе курсор прыгал бы в поле после
            // каждой загрузки (фокус — только у кладовщика, О4).
            disabled={autoFocus ? loading : false}
            showFeedbackIndicator={false}
            density="standard"
            autoFocus={autoFocus}
          />
        }
        chips={<FilterChips items={chips} value={tab} onChange={onTabChange} aria-label="Фильтр заданий на сборку" />}
      >
        {showWarehouse ? (
          <SelectField
            value={warehouseId}
            onChange={onWarehouseChange}
            options={[{ value: "", label: "Все склады" }, ...warehouses.map((w) => ({ value: w.id, label: w.name }))]}
            placeholder="Все склады"
            ariaLabel="Склад"
            className="w-[240px]"
          />
        ) : null}
      </FilterBar>
      {search ? (
        <Button variant="ghost" className="self-start" onClick={() => onSearchChange("")}>
          Сбросить поиск «{search}»
        </Button>
      ) : null}
    </div>
  );
}
